/**
 * **The address of a client's assigned-plan cycle grid** — the one thing a plan card does.
 *
 * Owner, 2 September 2026: "po prostu po kliknięciu na całą kartę ma tam przenosić". The
 * `Kalendarz →` affordance came off the card on 1 September and stays off; what replaced it is the
 * whole card, and this module is where the address it points at is decided.
 *
 * ── Why it is here rather than in either page's `model/` ──────────────────────────────────────
 * Two consumers, and they are in the same layer as each other: the client detail's summary column
 * and the roster's preview panel draw one card (`shared/ui/PlanCard`) and must lead to one place.
 * A page may not import another page, so a copy in each would be two spellings of one address with
 * nothing comparing them — which is exactly how the roster's card came to disagree with the
 * detail's about four other things before the card was unified.
 *
 * ── The plan calendar is a SCREEN, so both halves of the address are path parameters ─────────
 * Owner, 19 September 2026: the client's „Kalendarz" tab is the client's WHOLE calendar and the
 * plan's calendar is a view of one plan, framed like the programator. So this address is
 * `/klienci/$clientId/plan/$assignmentId` and carries no `?tab=` and no `?plan=` at all. The old
 * `?tab=calendar&plan=N` shape redirects (see the client route's `beforeLoad`), which is what keeps
 * a link written before today working; nothing in this app writes it any more.
 *
 * A plan with no assignment id still gets NO link rather than one pointing at the events calendar:
 * the assignment is half the path now, so there is no weaker version of this address to fall back
 * to — only a different destination.
 *
 * Pure: no React, no DOM, no `t`. The accessible name is composed by the caller, which has one.
 */

import type { LinkProps } from '@tanstack/react-router';

import type { RosterContextSearch } from './rosterContext';

export interface PlanCalendarLinkSource {
  /** The client whose plan this is. `null` for a row the roster has no customer id for. */
  readonly clientId: number | null | undefined;
  /** `AssignedPlanResponse.assignmentId`. Optional on the wire, so `undefined` is a real case. */
  readonly assignmentId: number | null | undefined;
  /**
   * The list the client was opened from, carried on so a click does not silently drop the roster's
   * search, filter and ordering. The roster's own preview panel has no list to carry — its sibling
   * links do not carry one either — and omits it.
   */
  readonly list?: RosterContextSearch;
}

/** A positive row id, or nothing. Both halves of this address are ids and nothing else. */
function rowId(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Where the plan card leads, or `null` when there is no plan to name — an empty card, a client the
 * roster has no id for, or an assignment the contract did not send an id for. `null` is the signal
 * that the card must not be a link at all: a card that looks pressable and goes nowhere is worse
 * than one that does not look pressable.
 */
export function planCalendarLink({
  clientId,
  assignmentId,
  list,
}: PlanCalendarLinkSource): LinkProps | null {
  const client = rowId(clientId);
  const assignment = rowId(assignmentId);
  if (client === null || assignment === null) return null;

  return {
    to: '/klienci/$clientId/plan/$assignmentId',
    params: { clientId: String(client), assignmentId: String(assignment) },
    search: { ...list },
  };
}
